/**
 * Settings — Your tools grid from /auth/me.capability_catalog.
 * Operator copy only (shared plain map with Overview).
 */

import { escapeHtml } from "./dom.js";
import {
  PLAIN_LABELS,
  PLAIN_BLURBS,
  CATEGORY_PLAIN,
  plainCapabilityEntry,
  verifiedResponseHonestyEntries,
} from "./overview-capability-entries.js";

const CATEGORY_ORDER = ["Before", "During", "After", "Platform"];

function capabilityCardHtml(item) {
  const plain = plainCapabilityEntry(item);
  const id = String((item && item.id) || "");
  const blurb =
    PLAIN_BLURBS[id] ||
    "Available when enabled for your account.";
  const action = plain.ready
    ? `<a class="btn secondary btn-tap sm" href="${escapeHtml(plain.href)}">Open</a>`
    : `<span class="muted" style="font-size:11px">${escapeHtml(plain.status)}</span>`;
  return `<article class="glass-panel glass-panel--inset settings-tool-card" style="padding:14px 16px;display:flex;flex-direction:column;gap:8px;min-height:138px;box-shadow:inset 0 1px 0 rgba(255,255,255,0.95),0 6px 18px rgba(59,130,246,0.08),0 14px 36px rgba(15,23,42,0.07)">
    <div class="row" style="justify-content:space-between;align-items:flex-start;gap:8px">
      <strong style="font-size:13px">${escapeHtml(plain.title || PLAIN_LABELS[id] || id)}</strong>
      <span class="badge ${escapeHtml(plain.statusCls)}">${escapeHtml(plain.status)}</span>
    </div>
    <p class="muted" style="font-size:12px;line-height:1.45;margin:0;flex:1">${escapeHtml(blurb)}</p>
    <div class="row" style="justify-content:flex-end;align-items:center;gap:8px">${action}</div>
  </article>`;
}

function honestyCardHtml(row) {
  const href = String((row && row.href) || "").trim();
  const action = href
    ? `<a class="btn secondary btn-tap sm" href="${escapeHtml(href)}">Open</a>`
    : `<span class="muted" style="font-size:11px">${escapeHtml((row && row.status) || "—")}</span>`;
  return `<article class="glass-panel glass-panel--inset settings-tool-card" style="padding:14px 16px;display:flex;flex-direction:column;gap:8px;min-height:138px;box-shadow:inset 0 1px 0 rgba(255,255,255,0.95),0 6px 18px rgba(59,130,246,0.08),0 14px 36px rgba(15,23,42,0.07)">
    <div class="row" style="justify-content:space-between;align-items:flex-start;gap:8px">
      <strong style="font-size:13px">${escapeHtml((row && row.title) || "Tool")}</strong>
      <span class="badge ${escapeHtml((row && row.statusCls) || "neutral")}">${escapeHtml((row && row.status) || "Preview")}</span>
    </div>
    <p class="muted" style="font-size:12px;line-height:1.45;margin:0;flex:1">${escapeHtml((row && row.blurb) || "")}</p>
    <div class="row" style="justify-content:flex-end;align-items:center;gap:8px">${action}</div>
  </article>`;
}

export function buildCapabilityOverviewHtml(catalog = []) {
  const items = Array.isArray(catalog) ? catalog : [];
  const enabled = items.filter((item) => item && item.enabled && item.href).length;
  const soon = items.filter((item) => item && item.enabled && !item.href).length;
  const disabled = items.filter((item) => item && !item.enabled).length;
  const sections = CATEGORY_ORDER.map((category) => {
    const rows = items.filter((item) => item && item.category === category);
    if (!rows.length) return "";
    return `<section style="margin-top:16px">
      <div class="row" style="justify-content:space-between;align-items:center">
        <h4 style="margin:0">${escapeHtml(CATEGORY_PLAIN[category] || category)}</h4>
        <span class="muted" style="font-size:11px">${rows.filter((item) => item.enabled && item.href).length}/${rows.length} ready</span>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px;margin-top:10px;padding:2px">
        ${rows.map(capabilityCardHtml).join("")}
      </div>
    </section>`;
  }).join("");
  const honestyRows = verifiedResponseHonestyEntries();
  const honestySec = honestyRows.length
    ? `<section style="margin-top:16px">
      <div class="row" style="justify-content:space-between;align-items:center">
        <h4 style="margin:0">Response network</h4>
        <span class="muted" style="font-size:11px">preview</span>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px;margin-top:10px;padding:2px">
        ${honestyRows.map(honestyCardHtml).join("")}
      </div>
    </section>`
    : "";
  return `<div class="settings-overview-sec" id="settings-sec-capabilities" data-settings-section="capabilities">
    <div class="glass-panel glass-panel--panel acc-settings-panel">
      <div class="ui-section-head">
        <div>
          <p class="acc-settings-panel__title">Tools</p>
          <h3 class="acc-settings-panel__head" style="margin:0">Your tools</h3>
          <p class="muted settings-admin-desc">Everything available for your role. Ready items open now; others stay listed so nothing is hidden.</p>
        </div>
        <div class="row" style="gap:6px;flex-wrap:wrap">
          <span class="badge online">${enabled} ready</span>
          <span class="badge neutral">${soon} coming soon</span>
          <span class="badge offline">${disabled} not available yet</span>
        </div>
      </div>
      ${sections}
      ${honestySec}
    </div>
  </div>`;
}
